/**
 * A3 flavor + deadpan-line loader.
 *
 * Slice A (2026-06-03): 50 generic strings, skill-agnostic.
 *   API: `getFlavorString(qr, rng?)` → random pick from generic[qr].
 *
 * Slice B (2026-06-03): adds 150 skill-family-keyed strings across
 *   fire_combat / stealth / persuasion (50 per family) plus 20 deadpan
 *   voice-lines for QR 4 + Failure. Existing callers continue to get
 *   the generic bucket; new callers pass `skillName` as the third
 *   positional and the loader resolves it via the seed's
 *   `skill_family_map`. Unmapped skills + missing-bucket cases fall
 *   back to generic so the component never renders an empty string.
 *
 * Tests inject `rng` for deterministic selection.
 */
import flavorData from '@seed/canonical/cinematics/qr_flavor_strings.json';

export type QrBucket = '1' | '2' | '3' | '4' | 'failure';
export type SkillFamily = 'fire_combat' | 'stealth' | 'persuasion' | 'generic';

interface QrFlavorSeed {
  version: string;
  schema: string;
  description: string;
  skill_family_map: Record<string, SkillFamily>;
  strings: Record<SkillFamily, Record<QrBucket, string[]>>;
  deadpan_voice_lines: Record<'4' | 'failure', string[]>;
}

const SEED = flavorData as QrFlavorSeed;

function bucketForQr(qr: 1 | 2 | 3 | 4 | null): QrBucket {
  return qr === null ? 'failure' : (String(qr) as QrBucket);
}

/**
 * Resolves the skill family for a given skill name. Unmapped names
 * return 'generic'.
 */
export function familyForSkill(skillName: string | undefined): SkillFamily {
  if (!skillName) {
    return 'generic';
  }
  return SEED.skill_family_map[skillName] ?? 'generic';
}

/**
 * Returns a flavor string for the given quality rating + (optional) skill
 * name. When skillName resolves to a family with strings for the QR
 * bucket, the family bucket is used; otherwise falls back to generic.
 *
 * Empty string if the seed bucket is missing/empty (defensive — never
 * expected in practice; covered by tests).
 *
 * @param qr         Quality rating 1-4 or null (Failure)
 * @param rng        Random source 0..1 (default Math.random)
 * @param skillName  Optional skill name; Slice B resolves to a family
 */
export function getFlavorString(
  qr: 1 | 2 | 3 | 4 | null,
  rng: () => number = Math.random,
  skillName?: string,
): string {
  const bucket = bucketForQr(qr);
  const family = familyForSkill(skillName);
  const familyStrings = SEED.strings[family]?.[bucket];
  const strings =
    familyStrings && familyStrings.length > 0
      ? familyStrings
      : SEED.strings.generic[bucket];
  if (!strings || strings.length === 0) {
    return '';
  }
  const index = Math.min(Math.floor(rng() * strings.length), strings.length - 1);
  return strings[index];
}

/**
 * Returns the number of strings authored for a given QR bucket within
 * a skill family (default 'generic'). Used by tests + by Slice B's
 * sample-without-replacement loop to detect exhaustion.
 */
export function getFlavorStringCount(
  qr: 1 | 2 | 3 | 4 | null,
  family: SkillFamily = 'generic',
): number {
  const bucket = bucketForQr(qr);
  return SEED.strings[family]?.[bucket]?.length ?? 0;
}

/**
 * Returns the full ordered bucket for a given QR + family. Useful for
 * tests + future Slice B sample-without-replacement implementations.
 */
export function getFlavorBucket(
  qr: 1 | 2 | 3 | 4 | null,
  family: SkillFamily = 'generic',
): readonly string[] {
  const bucket = bucketForQr(qr);
  return SEED.strings[family]?.[bucket] ?? [];
}

/**
 * Returns a deadpan voice-line for QR 4 + Failure (voice-mode appendix).
 * Returns empty string for QR 1-3 (no deadpan line — they get the
 * primary flavor only).
 */
export function getDeadpanLine(
  qr: 1 | 2 | 3 | 4 | null,
  rng: () => number = Math.random,
): string {
  let key: '4' | 'failure';
  if (qr === 4) {
    key = '4';
  } else if (qr === null) {
    key = 'failure';
  } else {
    return '';
  }
  const lines = SEED.deadpan_voice_lines[key];
  if (!lines || lines.length === 0) {
    return '';
  }
  const index = Math.min(Math.floor(rng() * lines.length), lines.length - 1);
  return lines[index];
}

/**
 * Returns the number of deadpan lines authored for the QR 4 or Failure
 * bucket (or 0 for QR 1-3).
 */
export function getDeadpanLineCount(qr: 1 | 2 | 3 | 4 | null): number {
  if (qr === 4) return SEED.deadpan_voice_lines['4']?.length ?? 0;
  if (qr === null) return SEED.deadpan_voice_lines.failure?.length ?? 0;
  return 0;
}
